import {
  Box,
  Chip,
  Paper,
  Typography,
  Link,
  Divider,
  IconButton,
} from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PersonIcon from "@mui/icons-material/Person";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import RemoveIcon from "@mui/icons-material/Remove";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import React, { useEffect, useRef, useState } from "react";
import { CustomerService } from "../../services/CustomerService";

const customerService = new CustomerService();

type CustomerPreferenceSummaryProps = {
  customerPreference: any;
  customerId: number;
  blockedCooks?: any[];
  preferredCooks?: any[];
};

const labelForGender = (value?: string) => {
  switch (value) {
    case "MALE":
      return "Male only";
    case "FEMALE":
      return "Female only";
    default:
      return "Any";
  }
};

const labelForReligion = (value?: string) => {
  switch (value) {
    case "NON_MUSLIM":
      return "Non-Muslim";
    default:
      return "Any";
  }
};

const labelForFood = (value?: string) => {
  switch (value) {
    case "VEG_ONLY":
      return "Veg only";
    default:
      return "Any";
  }
};

const labelForMultiday = (value?: string) => {
  if (!value) return "Not specified";
  if (value === "Cancel visit if preferred chef unavailable") {
    return "Cancel if preferred cook unavailable";
  }
  if (value === "Allocate any backup chef at scheduled time") {
    return "Allocate backup at same time";
  }
  if (value === "Confirm time change, then allocate preferred chef") {
    return "Confirm time change, then allocate preferred cook";
  }
  return value;
};

const LabelValue = ({ label, children }: any) => (
  <Box>
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ display: "block", mb: 0.5 }}
    >
      {label}
    </Typography>
    {children}
  </Box>
);

const CustomerPreferenceSummary = ({
  customerPreference,
  customerId,
  blockedCooks: blockedCooksProp,
  preferredCooks: preferredCooksProp,
}: CustomerPreferenceSummaryProps) => {
  const [fetchedPreferredCooks, setFetchedPreferredCooks] = useState<any[] | null>(null);

  useEffect(() => {
    if (preferredCooksProp !== undefined) return;
    if (!customerId) return;
    customerService
      .getCustomerCookPrefernces(customerId)
      .then((data: any) => setFetchedPreferredCooks(Array.isArray(data) ? data : []))
      .catch(() => setFetchedPreferredCooks([]));
  }, [customerId, preferredCooksProp]);

  const blockedCooks = blockedCooksProp ?? customerPreference?.blockedCooks ?? [];
  const visibleBlocked = blockedCooks.slice(0, 5);
  const remainingCount = blockedCooks.length - visibleBlocked.length;

  const preferredCooks =
    preferredCooksProp ??
    fetchedPreferredCooks ??
    customerPreference?.preferredCooks ??
    [];
  const sortedPreferred = [...preferredCooks].sort(
    (a: any, b: any) => (Number(a?.rank) || 0) - (Number(b?.rank) || 0)
  );
  const visiblePreferred = sortedPreferred.slice(0, 5);
  const remainingPreferredCount = sortedPreferred.length - visiblePreferred.length;

  const panelRef = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [isMinimized, setIsMinimized] = useState(
    () => typeof window !== "undefined" && window.innerWidth < 600
  );

  const dragState = useRef<any>(null);

  const clampToViewport = (x: number, y: number) => {
    const rect = panelRef.current?.getBoundingClientRect();
    const width = rect?.width ?? 0;
    const height = rect?.height ?? 0;
    return {
      x: Math.min(Math.max(8, x), Math.max(8, window.innerWidth - width - 8)),
      y: Math.min(Math.max(8, y), Math.max(8, window.innerHeight - height - 8)),
    };
  };

  useEffect(() => {
    if (!panelRef.current) return;
    const rect = panelRef.current.getBoundingClientRect();
    const nextX = Math.max(8, window.innerWidth - rect.width - 24);
    const nextY = Math.max(8, window.innerHeight - rect.height - 24);
    setPos({ x: nextX, y: nextY });
  }, []);

  // Keep the panel inside the viewport when it grows/shrinks or the window resizes
  useEffect(() => {
    const reclamp = () =>
      requestAnimationFrame(() => setPos((p) => clampToViewport(p.x, p.y)));
    reclamp();
    window.addEventListener("resize", reclamp);
    return () => window.removeEventListener("resize", reclamp);
  }, [isMinimized]);

  const onPointerDown = (e: React.PointerEvent) => {
    dragState.current = {
      startX: e.clientX,
      startY: e.clientY,
      originX: pos.x,
      originY: pos.y,
    };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragState.current) return;
    const dx = e.clientX - dragState.current.startX;
    const dy = e.clientY - dragState.current.startY;

    setPos(
      clampToViewport(
        dragState.current.originX + dx,
        dragState.current.originY + dy
      )
    );
  };

  const onPointerUp = (e: React.PointerEvent) => {
    dragState.current = null;
    (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
  };

  return (
    <Paper
      ref={panelRef}
      sx={{
        position: "fixed",
        top: pos.y,
        left: pos.x,
        width: { xs: "auto", sm: 420 },
        maxWidth: { xs: "calc(100vw - 16px)", sm: "92vw" },
        zIndex: 1400,
        borderRadius: 3,
        overflow: "hidden",
        boxShadow: "0 16px 40px rgba(0,0,0,0.2)",
      }}
    >
      {/* HEADER */}
      <Box
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        sx={{
          p: { xs: 1.25, sm: 2 },
          touchAction: "none",
          background: "linear-gradient(135deg, #2563EB, #1E40AF)",
          color: "#fff",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          cursor: "move",
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <AccessTimeIcon fontSize="small" />
          <Typography fontWeight={700}>
            Cook Preferences
          </Typography>
        </Box>

        <Box
          display="flex"
          alignItems="center"
          gap={1}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <IconButton
            size="small"
            onClick={() => setIsMinimized((v) => !v)}
            sx={{ color: "#E0E7FF" }}
            aria-label={isMinimized ? "Expand panel" : "Minimize panel"}
          >
            {isMinimized ? <ExpandMoreIcon /> : <RemoveIcon />}
          </IconButton>

          <Link
            href={`#/Customers/${customerId}/show/4`}
            underline="hover"
            sx={{ color: "#E0E7FF", fontWeight: 500 }}
          >
            Edit
          </Link>
        </Box>
      </Box>

      {/* CONTENT */}
      {!isMinimized && (
        <Box
          sx={{
            p: 2,
            maxHeight: { xs: "55vh", sm: "calc(100vh - 140px)" },
            overflowY: "auto",
          }}
        >
        {/* BASIC PREFS */}
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="caption" color="text.secondary">
            BASIC PREFERENCES
          </Typography>
          {!customerPreference?.genderPref && !customerPreference?.religionPref && !customerPreference?.foodTypePref && (
            <Chip label="Default preferences" size="small" sx={{ bgcolor: "#DBEAFE", color: "#1D4ED8", fontWeight: 600, height: 18, fontSize: 11 }} />
          )}
        </Box>

        <Box
          mt={1.5}
          display="grid"
          gridTemplateColumns="1fr 1fr"
          gap={2}
        >
          <LabelValue label="Gender">
            <Chip
              icon={<PersonIcon />}
              label={labelForGender(customerPreference?.genderPref)}
              size="small"
            />
          </LabelValue>

          <LabelValue label="Religion">
            <Chip
              label={labelForReligion(customerPreference?.religionPref)}
              size="small"
            />
          </LabelValue>

          <LabelValue label="Food Preference">
            <Chip
              icon={<RestaurantIcon />}
              label={labelForFood(customerPreference?.foodTypePref)}
              size="small"
            />
          </LabelValue>
        </Box>

        {/* PREFERRED COOKS */}
        <Divider sx={{ my: 2 }} />

        <LabelValue label="Preferred Cooks">
          {sortedPreferred.length > 0 ? (
            <Box display="flex" flexWrap="wrap" gap={1}>
              {visiblePreferred.map((c: any) => {
                const name = `${c?.cook?.firstName || ""} ${c?.cook?.lastName || ""}`.trim();
                return (
                  <Box
                    key={c?.cookId}
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.5,
                      bgcolor: "#DCFCE7",
                      color: "#166534",
                      borderRadius: "16px",
                      px: 1.25,
                      py: 0.25,
                      fontSize: 12,
                      fontWeight: 500,
                    }}
                  >
                    <Link
                      href={`#/Cooks/${c?.cookId}/show`}
                      underline="hover"
                      sx={{ color: "#166534", fontWeight: 700, fontSize: 12 }}
                    >
                      #{c?.cookId}
                    </Link>
                    {name && <span>{name}</span>}
                  </Box>
                );
              })}

              {remainingPreferredCount > 0 && (
                <Chip
                  label={`+${remainingPreferredCount} more`}
                  size="small"
                  variant="outlined"
                />
              )}
            </Box>
          ) : (
            <Typography fontSize={13} color="text.secondary" fontStyle="italic">
              No preferred chef found
            </Typography>
          )}
        </LabelValue>

        {/* BLOCKED COOKS */}
        {blockedCooks.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />

            <LabelValue label="Blocked Cooks">
              <Box display="flex" flexWrap="wrap" gap={1}>
                {visibleBlocked.map((c: any) => {
                  const name = `${c?.cook?.firstName || ""} ${c?.cook?.lastName || ""}`.trim();
                  const initial = name?.charAt(0)?.toUpperCase() || "#";

                  return (
                    <Chip
                      key={c?.cookId}
                      icon={
                        <Box
                          sx={{
                            width: 20,
                            height: 20,
                            borderRadius: "50%",
                            bgcolor: "#991B1B",
                            color: "#fff",
                            fontSize: 11,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {initial}
                        </Box>
                      }
                      label={`#${c?.cookId} ${name}`}
                      size="small"
                      sx={{
                        bgcolor: "#FEE2E2",
                        color: "#991B1B",
                        fontWeight: 500,
                      }}
                    />
                  );
                })}

                {remainingCount > 0 && (
                  <Chip
                    label={`+${remainingCount} more`}
                    size="small"
                    variant="outlined"
                  />
                )}
              </Box>
            </LabelValue>
          </>
        )}

        {/* MULTIDAY */}
        <Divider sx={{ my: 2 }} />

        <LabelValue label="If cook unavailable">
          <Typography fontSize={14} fontWeight={500}>
            {labelForMultiday(customerPreference?.multidayPref)}
          </Typography>
        </LabelValue>
        </Box>
      )}
    </Paper>
  );
};

export default CustomerPreferenceSummary;
